import { styled } from "@mui/material/styles";
import {
  Box,
  Button,
  Paper,
  Badge,
  IconButton,
  List,
  ListItem,
  InputBase,
} from "@mui/material";

// Refined color palette - minimal, clean, modern
export const theme = {
  // Primary colors
  primary: "#3b82f6", // Blue
  primaryLight: "#eff6ff",
  primaryDark: "#2563eb",

  // Semantic colors
  success: "#22c55e", // Green
  successLight: "#dcfce7",
  error: "#ef4444", // Red
  errorLight: "#fee2e2",
  warning: "#f59e0b", // Amber
  warningLight: "#fef3c7",

  // Surface colors
  background: "#f8fafc", // Slate 50
  surface: "#ffffff",
  border: "#e2e8f0", // Slate 200

  // Text colors
  textPrimary: "#1e293b", // Slate 800
  textSecondary: "#64748b", // Slate 500
  textMuted: "#94a3b8", // Slate 400

  // Gray scale (Slate)
  gray: {
    50: "#f8fafc",
    100: "#f1f5f9",
    200: "#e2e8f0",
    300: "#cbd5e1",
    400: "#94a3b8",
    500: "#64748b",
    600: "#475569",
    700: "#334155",
    800: "#1e293b",
    900: "#0f172a",
  },
};

// Main container - fits within template layout
export const StyledMainContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  height: "calc(100vh - 120px)",
  background: theme.background,
  fontFamily:
    '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
  overflow: "hidden",
  margin: 0,
  padding: 0,
}));

// Agent status bar - compact top bar
export const StyledAgentStatusBar = styled(Box)(() => ({
  height: "48px",
  padding: "0 16px",
  background: theme.surface,
  borderBottom: `1px solid ${theme.border}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexShrink: 0,
}));

// Content area below status bar
export const StyledContentArea = styled(Box)(() => ({
  display: "flex",
  flex: 1,
  overflow: "hidden",
}));

// Minimal sidebar - narrower
export const StyledSidebar = styled(Box)(() => ({
  width: "280px",
  background: theme.surface,
  borderRight: `1px solid ${theme.border}`,
  display: "flex",
  flexDirection: "column",
  height: "100%",
  overflow: "hidden",
  flexShrink: 0,
}));

// Clean header - minimal padding
export const StyledHeader = styled(Box)(() => ({
  padding: "12px 16px",
  background: theme.surface,
  borderBottom: `1px solid ${theme.border}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

// Minimal notification button
export const StyledNotificationButton = styled(IconButton)(() => ({
  color: theme.textSecondary,
  padding: "8px",
  transition: "all 0.2s ease",
  "&:hover": {
    background: theme.gray[100],
    color: theme.textPrimary,
  },
  "& .MuiBadge-badge": {
    background: theme.error,
    color: theme.surface,
    fontSize: "11px",
    height: "18px",
    minWidth: "18px",
  },
}));

// Session list container
export const StyledSessionList = styled(List)(() => ({
  flex: 1,
  overflow: "auto",
  padding: "8px",
  "&::-webkit-scrollbar": {
    width: "4px",
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    background: theme.gray[300],
    borderRadius: "2px",
    "&:hover": {
      background: theme.gray[400],
    },
  },
}));

// Clean session card - minimal design
export const StyledSessionCard = styled(ListItem, {
  shouldForwardProp: (prop) => !['active', 'pending', 'hasUnread'].includes(prop),
})(
  ({ active, pending, hasUnread }) => ({
    background: active
      ? theme.primaryLight
      : hasUnread
      ? "#f0f9ff"
      : theme.surface,
    margin: "4px 0",
    borderRadius: "8px",
    padding: "12px",
    cursor: "pointer",
    transition: "all 0.15s ease",
    border: active ? `1.5px solid ${theme.primary}` : "1.5px solid transparent",
    position: "relative",
    overflow: "visible",
    "&:hover": {
      background: active ? theme.primaryLight : theme.gray[50],
      borderColor: active ? theme.primary : theme.gray[200],
    },
    "& .MuiListItemText-primary": {
      fontWeight: active || hasUnread ? 600 : 500,
      color: theme.textPrimary,
      fontSize: "14px",
    },
    "& .MuiListItemText-secondary": {
      color: theme.textSecondary,
      fontSize: "12px",
    },
  })
);

// Minimal badge
export const StyledBadge = styled(Badge)(() => ({
  "& .MuiBadge-badge": {
    background: theme.primary,
    color: theme.surface,
    fontWeight: 600,
    fontSize: "11px",
    height: "18px",
    minWidth: "18px",
    padding: "0 5px",
  },
}));

// Clean button - no gradient
export const StyledButton = styled(Button)(({ color }) => ({
  background:
    color === "error"
      ? theme.error
      : color === "success"
      ? theme.success
      : theme.primary,
  color: theme.surface,
  borderRadius: "8px",
  padding: "8px 16px",
  fontWeight: 500,
  textTransform: "none",
  fontSize: "14px",
  boxShadow: "none",
  transition: "all 0.15s ease",
  border: "none",
  "&:hover": {
    background:
      color === "error"
        ? "#dc2626"
        : color === "success"
        ? "#16a34a"
        : theme.primaryDark,
    boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
  },
  "&:active": {
    transform: "scale(0.98)",
  },
  "&.Mui-disabled": {
    background: theme.gray[200],
    color: theme.gray[400],
  },
}));

// Accept button - clean green
export const StyledAcceptButton = styled(StyledButton)(() => ({
  background: theme.success,
  "&:hover": {
    background: "#16a34a",
  },
}));

// Decline button - clean red
export const StyledDeclineButton = styled(StyledButton)(() => ({
  background: theme.error,
  "&:hover": {
    background: "#dc2626",
  },
}));

// Chat area - full height
export const StyledChatArea = styled(Box)(() => ({
  flexGrow: 1,
  background: theme.surface,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
}));

// Chat header - minimal
export const StyledChatHeader = styled(Box)(() => ({
  padding: "12px 24px",
  background: theme.surface,
  borderBottom: `1px solid ${theme.border}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  minHeight: "60px",
}));

// Messages container - softer background
export const StyledMessagesContainer = styled(Box)(() => ({
  flexGrow: 1,
  overflowY: "auto",
  padding: "20px 24px",
  background: theme.gray[50],
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    background: theme.gray[300],
    borderRadius: "3px",
    "&:hover": {
      background: theme.gray[400],
    },
  },
}));

// Clean message bubble - softer styling
export const StyledMessageBubble = styled(Box, {
  shouldForwardProp: (prop) => !['isAgent', 'isBot', 'isSystem'].includes(prop),
})(
  ({ isAgent, isBot, isSystem }) => {
    if (isSystem) {
      return {
        alignSelf: "center",
        maxWidth: "85%",
        padding: "8px 16px",
        borderRadius: "16px",
        background: theme.gray[100],
        color: theme.textSecondary,
        fontSize: "13px",
        textAlign: "center",
        border: `1px dashed ${theme.gray[300]}`,
      };
    }

    if (isBot) {
      return {
        alignSelf: "flex-start",
        maxWidth: "70%",
        padding: "12px 16px",
        borderRadius: "16px 16px 16px 4px",
        background: "#f5f3ff",
        color: theme.textPrimary,
        border: `1px solid #e9d5ff`,
        wordBreak: "break-word",
        "& .message-time": {
          fontSize: "11px",
          marginTop: "6px",
          color: "#a78bfa",
        },
      };
    }

    return {
      alignSelf: isAgent ? "flex-end" : "flex-start",
      maxWidth: "70%",
      padding: "12px 16px",
      borderRadius: isAgent ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
      background: isAgent ? theme.primary : theme.surface,
      color: isAgent ? theme.surface : theme.textPrimary,
      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
      border: isAgent ? "none" : `1px solid ${theme.gray[200]}`,
      wordBreak: "break-word",
      "& .message-time": {
        fontSize: "11px",
        marginTop: "6px",
        color: isAgent ? "rgba(255,255,255,0.7)" : theme.textMuted,
      },
    };
  }
);

// Floating input area - card style
export const StyledInputArea = styled(Box)(() => ({
  padding: "16px 24px",
  background: theme.surface,
  borderTop: `1px solid ${theme.border}`,
}));

// Floating input wrapper
export const StyledInputWrapper = styled(Paper)(() => ({
  display: "flex",
  alignItems: "center",
  border: `1px solid ${theme.border}`,
  borderRadius: "12px",
  padding: "8px 12px",
  background: theme.surface,
  boxShadow: "none",
  transition: "all 0.15s ease",
  "&:focus-within": {
    borderColor: theme.primary,
    boxShadow: `0 0 0 3px ${theme.primaryLight}`,
  },
}));

// Minimal input field
export const StyledInput = styled(InputBase)(() => ({
  flexGrow: 1,
  fontSize: "14px",
  fontFamily: "inherit",
  "& .MuiInputBase-input": {
    padding: "6px 8px",
  },
  "& .MuiInputBase-input::placeholder": {
    color: theme.textMuted,
    opacity: 1,
  },
}));

// Send button - compact
export const StyledSendButton = styled(IconButton)(() => ({
  color: theme.primary,
  padding: "8px",
  transition: "all 0.15s ease",
  "&:hover": {
    background: theme.primaryLight,
  },
  "&.Mui-disabled": {
    color: theme.gray[300],
  },
}));

// Clean notification popover
export const StyledNotificationPopover = styled(Paper)(() => ({
  minWidth: "320px",
  maxWidth: "380px",
  maxHeight: "480px",
  overflow: "hidden",
  borderRadius: "12px",
  boxShadow: "0 10px 40px rgba(0,0,0,0.12)",
  border: `1px solid ${theme.border}`,
}));

// Notification item - minimal
export const StyledNotificationItem = styled(Box)(() => ({
  padding: "12px",
  margin: "8px",
  background: theme.gray[50],
  borderRadius: "8px",
  border: `1px solid ${theme.border}`,
  transition: "all 0.15s ease",
  "&:hover": {
    background: theme.gray[100],
  },
}));

// Action buttons container
export const StyledActionButtons = styled(Box)(() => ({
  display: "flex",
  gap: "4px",
  alignItems: "center",
}));

// Clean icon button
export const StyledFloatingButton = styled(IconButton)(({ color }) => ({
  background: color || theme.primary,
  color: theme.surface,
  width: "36px",
  height: "36px",
  transition: "all 0.15s ease",
  "&:hover": {
    background: color || theme.primaryDark,
    transform: "scale(1.05)",
  },
  "&:active": {
    transform: "scale(0.95)",
  },
}));

// Action icon button - minimal
export const StyledActionIconButton = styled(IconButton)(() => ({
  color: theme.textSecondary,
  padding: "8px",
  transition: "all 0.15s ease",
  "&:hover": {
    background: theme.gray[100],
    color: theme.textPrimary,
  },
}));

// Minimal chip/tag
export const StyledChip = styled(Box)(({ status }) => ({
  display: "inline-flex",
  alignItems: "center",
  padding: "2px 10px",
  borderRadius: "10px",
  fontSize: "12px",
  fontWeight: 500,
  background:
    status === "active"
      ? theme.successLight
      : status === "pending"
      ? theme.warningLight
      : theme.gray[100],
  color:
    status === "active"
      ? "#16a34a"
      : status === "pending"
      ? "#d97706"
      : theme.textSecondary,
}));

// Status dot
export const StyledStatusDot = styled(Box)(({ status }) => ({
  width: "8px",
  height: "8px",
  borderRadius: "50%",
  background:
    status === "online" || status === "active"
      ? theme.success
      : status === "busy"
      ? theme.warning
      : status === "away" || status === "pending"
      ? "#fbbf24"
      : theme.gray[400],
  flexShrink: 0,
}));

// Empty state - centered and clean
export const StyledEmptyState = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  color: theme.textMuted,
  padding: "48px 24px",
  "& svg": {
    fontSize: "56px",
    color: theme.gray[300],
    marginBottom: "20px",
  },
  "& h3": {
    color: theme.textPrimary,
    fontSize: "18px",
    fontWeight: 600,
    marginBottom: "8px",
    marginTop: 0,
  },
  "& p": {
    color: theme.textSecondary,
    fontSize: "14px",
    textAlign: "center",
    maxWidth: "280px",
    margin: 0,
    lineHeight: 1.5,
  },
}));

// Loading skeleton
export const StyledSkeleton = styled(Box)(() => ({
  background: `linear-gradient(90deg, ${theme.gray[100]} 25%, ${theme.gray[200]} 50%, ${theme.gray[100]} 75%)`,
  backgroundSize: "200% 100%",
  animation: "loading 1.5s infinite",
  borderRadius: "8px",
  "@keyframes loading": {
    "0%": { backgroundPosition: "200% 0" },
    "100%": { backgroundPosition: "-200% 0" },
  },
}));

// Queue entry card
export const StyledQueueEntry = styled(Box)(({ priority }) => ({
  padding: "12px",
  marginBottom: "8px",
  background:
    priority === "high"
      ? "#fef2f2"
      : priority === "medium"
      ? theme.warningLight
      : theme.primaryLight,
  borderRadius: "8px",
  borderLeft: `3px solid ${
    priority === "high"
      ? theme.error
      : priority === "medium"
      ? theme.warning
      : theme.primary
  }`,
  transition: "all 0.15s ease",
  "&:hover": {
    transform: "translateX(2px)",
  },
}));

// Bot summary card
export const StyledBotSummary = styled(Box)(() => ({
  margin: "12px 24px",
  padding: "12px 16px",
  background: "#f0f9ff",
  borderRadius: "8px",
  border: `1px solid #bae6fd`,
}));

// Tab button - underline style
export const StyledTabButton = styled(Button)(({ active }) => ({
  color: active ? theme.primary : theme.textSecondary,
  fontWeight: active ? 600 : 500,
  fontSize: "13px",
  textTransform: "none",
  padding: "8px 12px",
  minWidth: "auto",
  borderRadius: 0,
  borderBottom: active ? `2px solid ${theme.primary}` : "2px solid transparent",
  transition: "all 0.15s ease",
  "&:hover": {
    background: "transparent",
    color: theme.primary,
  },
}));

// Divider text
export const StyledDivider = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  margin: "16px 0",
  "& span": {
    fontSize: "11px",
    fontWeight: 600,
    color: theme.textMuted,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    padding: "0 12px",
    background: theme.gray[50],
  },
  "&::before, &::after": {
    content: '""',
    flex: 1,
    height: "1px",
    background: theme.gray[200],
  },
}));

// Typing indicator container
export const StyledTypingIndicator = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: "4px",
  padding: "12px 16px",
  background: theme.gray[100],
  borderRadius: "16px",
  alignSelf: "flex-start",
  "& .dot": {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    background: theme.textMuted,
    animation: "bounce 1.4s infinite ease-in-out",
  },
  "& .dot:nth-of-type(1)": {
    animationDelay: "-0.32s",
  },
  "& .dot:nth-of-type(2)": {
    animationDelay: "-0.16s",
  },
  "@keyframes bounce": {
    "0%, 80%, 100%": {
      transform: "scale(0.8)",
      opacity: 0.5,
    },
    "40%": {
      transform: "scale(1)",
      opacity: 1,
    },
  },
}));

// Accept banner for pending sessions
export const StyledAcceptBanner = styled(Box)(() => ({
  padding: "12px 24px",
  background: theme.warningLight,
  borderBottom: `1px solid #fde68a`,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "16px",
}));
